import axios, { AxiosInstance, AxiosError } from 'axios';
import type {
  StylesResponse,
  TryHairstyleRequest,
  TryHairstyleDualRequest,
  TryHairstyleDualResponse,
  BookingPreviewRequest,
  HairQuizAnswers,
  HairQuizResponse,
  HairFaqRequest,
  HairFaqResponse,
  GeneratedImage
} from '../types';

interface ApiError extends Error {
  status?: number;
  detail?: string;
  code?: string;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const SINGLE_TIMEOUT = 180000;
const DUAL_TIMEOUT = 300000;

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 90000,
      headers: { 'Content-Type': 'application/json' },
    });

    this.client.interceptors.request.use(
      (config) => {
        if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
          const initData = window.Telegram.WebApp.initData;
          if (initData) {
            config.headers['X-Telegram-InitData'] = initData;
          } else {
            config.headers['X-Sheriff-Preview'] = 'browser';
          }
        } else if (typeof window !== 'undefined') {
          config.headers['X-Sheriff-Preview'] = 'browser';
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        console.error('API Error:', error.response?.data || error.message);
        return Promise.reject(error);
      }
    );
  }

  private getLongTimeoutClient(timeout: number = SINGLE_TIMEOUT): AxiosInstance {
    const client = axios.create({
      baseURL: API_BASE_URL,
      timeout: timeout,
      headers: { 'Content-Type': 'application/json' },
    });

    client.interceptors.request.use(
      (config) => {
        if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
          const initData = window.Telegram.WebApp.initData;
          if (initData) {
            config.headers['X-Telegram-InitData'] = initData;
          } else {
            config.headers['X-Sheriff-Preview'] = 'browser';
          }
        } else if (typeof window !== 'undefined') {
          config.headers['X-Sheriff-Preview'] = 'browser';
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    return client;
  }

  async getStyles(): Promise<StylesResponse> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const response = await this.client.get('/styles', {
        signal: controller.signal,
        headers: { 'Cache-Control': 'no-store' },
      });
      clearTimeout(timeoutId);

      const data = response.data;
      let stylesArray: unknown[] = [];
      if (Array.isArray(data)) {
        stylesArray = data;
      } else if (data && Array.isArray(data.styles)) {
        stylesArray = data.styles;
      }

      return { styles: stylesArray as StylesResponse['styles'] };
    } catch (error) {
      clearTimeout(timeoutId);
      if ((error as Error).name === 'AbortError' || (error as Error).name === 'CanceledError') {
        throw new Error('styles_load_timeout');
      }
      throw error;
    }
  }

  async tryHairstyle(request: TryHairstyleRequest): Promise<{ imageUrl: string; serverImageUrl?: string; imageBlob: Blob; generatedImageId?: string }> {
    if (!request.style_id && !request.custom_description && !request.reference_photo) {
      throw new Error('hairstyle_input_required');
    }

    try {
      const formData = new FormData();
      formData.append('photo', request.photo);
      if (request.style_id) formData.append('style_id', request.style_id);
      if (request.custom_description) formData.append('custom_description', request.custom_description);
      if (request.reference_photo) formData.append('reference_photo', request.reference_photo);
      if (request.mode) formData.append('mode', request.mode);
      if (request.user_id) formData.append('user_id', request.user_id);
      if (request.variant) formData.append('variant', request.variant);

      const longClient = this.getLongTimeoutClient(SINGLE_TIMEOUT);
      const response = await longClient.post('/hairstyle/try', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob',
        timeout: SINGLE_TIMEOUT,
      });

      if (!(response.data instanceof Blob)) {
        throw new Error('invalid_image_response');
      }

      let blob = response.data;
      if (blob.size === 0) {
        throw new Error('empty_image_response');
      }

      if (!blob.type || blob.type === 'application/octet-stream') {
        blob = new Blob([blob], { type: 'image/jpeg' });
      }

      const imageUrl = URL.createObjectURL(blob);
      const generatedImageId = response.headers['x-generated-image-id'] as string | undefined;
      
      // Build server URL for Telegram WebView (blob URLs don't work there)
      let serverImageUrl: string | undefined;
      if (generatedImageId) {
        serverImageUrl = `${window.location.origin}/api/generated/${generatedImageId}`;
      }

      return { imageUrl, serverImageUrl, imageBlob: blob, generatedImageId };
    } catch (error) {
      throw await this.normalizeError(error as AxiosError);
    }
  }

  async tryHairstyleDual(request: TryHairstyleDualRequest): Promise<TryHairstyleDualResponse> {
    const mode = request.mode || (request.reference_photo ? 'reference' : (request.style_id ? 'preset' : 'custom'));
    
    if (mode === 'preset' && !request.style_id) {
      throw new Error('style_id_required');
    } else if (mode === 'custom' && !request.custom_description?.trim()) {
      throw new Error('custom_description_required');
    } else if (mode === 'reference' && !request.reference_photo) {
      throw new Error('reference_photo_required');
    }

    try {
      const formData = new FormData();
      formData.append('front_photo', request.front_photo);
      formData.append('profile_photo', request.profile_photo);
      
      if (request.mode) {
        formData.append('mode', request.mode);
      } else {
        const autoMode = request.reference_photo ? 'reference' : (request.style_id ? 'preset' : 'custom');
        formData.append('mode', autoMode);
      }
      
      if (request.style_id) formData.append('style_id', request.style_id);
      if (request.custom_description) formData.append('custom_description', request.custom_description);
      if (request.reference_photo) formData.append('reference_photo', request.reference_photo);
      if (request.user_id) formData.append('user_id', request.user_id);
      if (request.variant) formData.append('variant', request.variant);
      if (request.wish) formData.append('wish', request.wish);

      const longClient = this.getLongTimeoutClient(DUAL_TIMEOUT);
      const response = await longClient.post<TryHairstyleDualResponse>('/hairstyle/try-dual', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: DUAL_TIMEOUT,
      });

      return response.data;
    } catch (error) {
      throw await this.normalizeError(error as AxiosError);
    }
  }

  async suggestHaircutsByQuiz(answers: HairQuizAnswers): Promise<HairQuizResponse> {
    try {
      const response = await this.client.post<HairQuizResponse>('/hair-quiz/suggest', answers);
      return response.data;
    } catch (error) {
      throw await this.normalizeError(error as AxiosError);
    }
  }

  async askHairFaq(payload: HairFaqRequest): Promise<HairFaqResponse> {
    try {
      const response = await this.client.post<HairFaqResponse>('/faq/ask', payload);
      return response.data;
    } catch (error) {
      throw await this.normalizeError(error as AxiosError);
    }
  }

  async askHairFaqWithImages(params: {
    question: string;
    face_shape?: string;
    hair_type?: string;
    user_id?: string;
    session_id?: string;
    images: File[];
  }): Promise<HairFaqResponse> {
    try {
      const formData = new FormData();
      formData.append('question', params.question);
      if (params.face_shape) formData.append('face_shape', params.face_shape);
      if (params.hair_type) formData.append('hair_type', params.hair_type);
      if (params.user_id) formData.append('user_id', params.user_id);
      if (params.session_id) formData.append('session_id', params.session_id);
      
      params.images.forEach((image: File) => {
        formData.append('images', image);
      });

      const response = await this.client.post<HairFaqResponse>('/faq/ask-with-images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      
      return response.data;
    } catch (error) {
      throw await this.normalizeError(error as AxiosError);
    }
  }

  async clearFaqMemory(userId: string): Promise<{ success: boolean }> {
    try {
      const response = await this.client.post<{ success: boolean }>('/faq/clear-memory', {
        user_id: userId
      });
      return response.data;
    } catch (error) {
      throw await this.normalizeError(error as AxiosError);
    }
  }

  async applyForTraining(payload: { name: string; phone: string; source?: string }): Promise<{ success: boolean }> {
    try {
      const response = await this.client.post<{ success: boolean }>('/training/apply', payload);
      return response.data;
    } catch (error) {
      throw await this.normalizeError(error as AxiosError);
    }
  }

  async previewHairstyleForBooking(request: BookingPreviewRequest): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('selfie', request.selfie);
      if (request.reference) formData.append('reference', request.reference);
      if (request.custom_description) formData.append('custom_description', request.custom_description);
      if (request.master_id) formData.append('master_id', request.master_id);
      if (request.user_id) formData.append('user_id', request.user_id);

      const response = await this.client.post('/booking/preview-hairstyle', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob',
        timeout: 120000,
      });

      if (!(response.data instanceof Blob)) {
        throw new Error('invalid_image_response');
      }

      let blob = response.data;
      if (!blob.type || blob.type === 'application/octet-stream') {
        blob = new Blob([blob], { type: 'image/jpeg' });
      }

      return URL.createObjectURL(blob);
    } catch (error) {
      throw await this.normalizeError(error as AxiosError);
    }
  }

  async getUserHistory(userId: string, limit: number = 20): Promise<{ images: GeneratedImage[] }> {
    try {
      const response = await this.client.get(`/user/${userId}/history`, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      throw await this.normalizeError(error as AxiosError);
    }
  }

  async sendToTelegram(imageId: string, userId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.client.post('/send-to-telegram', {
        image_id: imageId,
        user_id: userId
      });
      return response.data;
    } catch (error) {
      throw await this.normalizeError(error as AxiosError);
    }
  }

  async sendGeneratedToTelegram(imageId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.client.post(`/generated/${imageId}/send-to-telegram`);
      return response.data;
    } catch (error) {
      throw await this.normalizeError(error as AxiosError);
    }
  }

  async detectFaceShape(photo: File): Promise<{
    face_shape: string;
    confidence: number;
    tips: string;
    recommended_style_ids: string[];
  }> {
    try {
      const formData = new FormData();
      formData.append('photo', photo);

      const response = await this.client.post('/vision/face-shape', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });

      return response.data;
    } catch (error) {
      throw await this.normalizeError(error as AxiosError);
    }
  }

  private async normalizeError(error: AxiosError): Promise<never> {
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      const timeoutError: ApiError = new Error(
        'request_timeout'
      );
      timeoutError.status = 504;
      timeoutError.detail = timeoutError.message;
      timeoutError.code = 'timeout';
      throw timeoutError;
    }

    if (error.response?.status === 401) {
      const authError: ApiError = new Error(
        'telegram_auth_required'
      );
      authError.status = 401;
      authError.detail = authError.message;
      authError.code = 'unauthorized';
      throw authError;
    }

    if (error.response) {
      const data: any = error.response.data;
      const detailObj = typeof data?.detail === 'object' ? data.detail : null;
      
      let msg =
        (detailObj?.message as string) ||
        (typeof data?.detail === 'string' && data.detail) ||
        (typeof data?.message === 'string' && data.message) ||
        (typeof data === 'string' && data) ||
        error.message ||
        'hairstyle_generation_failed';

      const apiError: ApiError = new Error(msg);
      apiError.status = error.response.status;
      apiError.detail = msg;
      apiError.code = (detailObj?.error_code as string) || (data?.error_code as string) || undefined;
      throw apiError;
    }

    const genericError: ApiError = new Error(
      error.message || 'unknown_api_error'
    );
    genericError.status = (error as any).response?.status;
    genericError.detail = genericError.message;
    throw genericError;
  }
}

export const apiClient = new ApiClient();
